/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Container,
  Box,
  Typography,
  Stack,
  Skeleton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  useForm,
  getSubmitButtonProps,
  useApiClient,
  useFetch,
  RichTextEditor,
} from '@marageti/z4-lib';
import { useNavigate, useParams } from 'react-router-dom';
import { Partner } from '@marageti/z4-sdk/lib/travel';
import { useSnackbar } from 'notistack';
import { Controller } from 'react-hook-form';

const schema = z.object({
  notes: z.string(),
});

const MatchingNotesForm = ({
  partner,
  onSubmit,
}: { partner: Partner, onSubmit: (data: any) => void }) => {
  const {
    formState: {
      errors,
      isSubmitting,
      isValid,
      isDirty,
    },
    handleSubmit,
    control,
  } = useForm({
    resolver: zodResolver(schema),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Controller
          control={control}
          name="notes"
          render={({ field }) => (
            <RichTextEditor
              defaultValue={partner.notes}
              editorName="notes-editor"
              error={!!errors.notes}
              helperText={errors.notes?.message as string | undefined}
              label="Internal Notes (Zicasso Only)"
              onChange={field.onChange}
              useLinks
              useLists
              sx={{
                minHeight: { desktop: '550px' },
                maxHeight: '550px',
              }}
            />
          )}
        />
        <Stack direction="row" spacing={2}>
          <LoadingButton
            variant="contained"
            type="submit"
            {...getSubmitButtonProps(isSubmitting, (isValid && isDirty))}
          >
            Save
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  );
};

const MatchingNotes = () => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { partnerId } = useParams<{ partnerId: string }>();

  const { data: partner, mutate } = useFetch<Partner>(apiClient.partnersClient.getPartner, [partnerId], true);

  const onSubmit = async (data: any) => {
    try {
      await apiClient.partnersClient.putPartnerProfile(partnerId!, {
        ...partner,
        ...data,
      });
      mutate();
      navigate(-1);
      enqueueSnackbar('Notes Updated.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Updating partner notes failed.', { variant: 'default' });
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{
        pt: { desktop: 5, mobile: 2 },
        pb: 9.5,
      }}
      >
        <Typography variant="h2" sx={{ mb: 3 }}>Matching Notes</Typography>
        {
          // Zod doesn't handle async defualt values well, so we need to wait for the partner to load
          partner ? (
            <MatchingNotesForm partner={partner} onSubmit={onSubmit} />
          ) : (
            <Skeleton height={300} />
          )
        }
      </Box>
    </Container>
  );
};

export default MatchingNotes;
